import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { PersonConsentForm, PersonDocument } from "../../api/360Files";
import {
  useDeletePersonDocument,
  useGetPersonDocuments,
} from "../../api/360FilesAPI";
import { useApplicationToast } from "../../webcontent/ApplicationToastProvider";
import { Modal } from "../../shared-components/Modal";
import ConsentFormSection from "../ConsentFormSection";
import DocsFormSection from "../DocsFormSection";
import DragDropUploader from "../docs/DragDropUploader";
import { usePermissionValidator } from "../../api/UserPermissionValidators";

type UploadModalType = "consent-form" | "other" | "resume";

const UPLOAD_MODAL_TITLES: Record<UploadModalType, string> = {
  "consent-form": "Add Consent Form",
  other: "Add Certificates",
  resume: "Add Resume",
};

const DELETE_ERROR_MESSAGES: Record<UploadModalType, string> = {
  "consent-form": "Failed to delete consent form. Please try again.",
  other: "Failed to delete certificate. Please try again.",
  resume: "Failed to delete resume. Please try again.",
};

const INITIAL_DELETING_COUNTS: Record<UploadModalType, number> = {
  "consent-form": 0,
  other: 0,
  resume: 0,
};

/** Server stores certificate uploads under type "other". */
const CERTIFICATE_DOC_TYPE = "other";

function filterDocsByType(
  docs: PersonDocument[],
  type: string,
  pendingDeletedUrls: Set<string>
): PersonDocument[] {
  return (docs ?? [])
    .filter((doc) => doc.type === type)
    .filter((doc) => !pendingDeletedUrls.has(doc.url));
}

interface PersonDocumentsSectionProps {
  personUuid: string;
  consentForms?: PersonConsentForm[];
}

const PersonDocumentsSection: React.FC<PersonDocumentsSectionProps> = ({
  personUuid,
  consentForms = [],
}) => {
  const { canEdit360Reports } = usePermissionValidator();
  const { showPushToast } = useApplicationToast();
  const { mutateAsync: deletePersonDocument } = useDeletePersonDocument();
  const { mutateAsync: personDocsGet, reset: resetPersonDocs } =
    useGetPersonDocuments();

  const [openUploadModal, setOpenUploadModal] =
    useState<UploadModalType | null>(null);
  const [localConsentDocs, setLocalConsentDocs] = useState<PersonDocument[]>(
    []
  );
  const [localCertificateDocs, setLocalCertificateDocs] = useState<
    PersonDocument[]
  >([]);
  const [localResumeDocs, setLocalResumeDocs] = useState<PersonDocument[]>([]);
  const [isLoadingDocs, setIsLoadingDocs] = useState(false);
  const [docsLoadError, setDocsLoadError] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [deletingCounts, setDeletingCounts] = useState(INITIAL_DELETING_COUNTS);
  const pendingDeletedUrlsRef = useRef<Set<string>>(new Set());
  const personUuidRef = useRef(personUuid);
  const docsFetchGenerationRef = useRef(0);
  personUuidRef.current = personUuid;

  const applyPersonDocs = useCallback((docs: PersonDocument[]) => {
    const pending = pendingDeletedUrlsRef.current;
    setLocalConsentDocs(filterDocsByType(docs, "consent-form", pending));
    setLocalCertificateDocs(
      filterDocsByType(docs, CERTIFICATE_DOC_TYPE, pending)
    );
    setLocalResumeDocs(filterDocsByType(docs, "resume", pending));
  }, []);

  useLayoutEffect(() => {
    setOpenUploadModal(null);
  }, [personUuid]);

  useEffect(() => {
    if (!personUuid) return;

    let cancelled = false;
    const generation = ++docsFetchGenerationRef.current;
    setIsLoadingDocs(true);
    setDocsLoadError(false);
    resetPersonDocs();
    setLocalConsentDocs([]);
    setLocalCertificateDocs([]);
    setLocalResumeDocs([]);
    pendingDeletedUrlsRef.current.clear();
    setDeletingCounts(INITIAL_DELETING_COUNTS);

    void personDocsGet({ personUuid, is_attachment: true })
      .then((docs) => {
        if (
          !cancelled &&
          generation === docsFetchGenerationRef.current &&
          personUuidRef.current === personUuid
        ) {
          applyPersonDocs(docs);
        }
      })
      .catch(() => {
        if (
          !cancelled &&
          generation === docsFetchGenerationRef.current &&
          personUuidRef.current === personUuid
        ) {
          setDocsLoadError(true);
        }
      })
      .finally(() => {
        if (
          !cancelled &&
          generation === docsFetchGenerationRef.current &&
          personUuidRef.current === personUuid
        ) {
          setIsLoadingDocs(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    personUuid,
    personDocsGet,
    resetPersonDocs,
    applyPersonDocs,
    loadAttempt,
  ]);

  const handleRetryLoadDocs = useCallback(() => {
    setLoadAttempt((attempt) => attempt + 1);
  }, []);

  const handleDeleteDoc = useCallback(
    async (
      doc: PersonDocument,
      section: UploadModalType,
      setLocalDocs: React.Dispatch<React.SetStateAction<PersonDocument[]>>
    ) => {
      if (pendingDeletedUrlsRef.current.has(doc.url)) {
        return;
      }

      const deleteForPersonUuid = personUuid;
      pendingDeletedUrlsRef.current.add(doc.url);
      setLocalDocs((prev) => prev.filter((d) => d.url !== doc.url));
      setDeletingCounts((prev) => ({ ...prev, [section]: prev[section] + 1 }));

      try {
        await deletePersonDocument({
          personUuid: deleteForPersonUuid,
          document_type: doc.type,
          filename: doc.filename,
        });
      } catch {
        if (personUuidRef.current !== deleteForPersonUuid) {
          return;
        }
        pendingDeletedUrlsRef.current.delete(doc.url);
        setLocalDocs((prev) => {
          if (prev.some((d) => d.url === doc.url)) {
            return prev;
          }
          return [...prev, doc];
        });
        showPushToast?.({
          message: DELETE_ERROR_MESSAGES[section],
          duration: 4000,
          type: "error",
        });
      } finally {
        if (personUuidRef.current === deleteForPersonUuid) {
          setDeletingCounts((prev) => ({
            ...prev,
            [section]: prev[section] - 1,
          }));
        }
      }
    },
    [deletePersonDocument, personUuid, showPushToast]
  );

  const handleUploadComplete = useCallback(
    async (_filenames: string[], allSucceeded: boolean) => {
      const uploadForPersonUuid = personUuid;
      const generation = ++docsFetchGenerationRef.current;
      setIsLoadingDocs(true);

      const isStale = () =>
        personUuidRef.current !== uploadForPersonUuid ||
        generation !== docsFetchGenerationRef.current;

      try {
        const docs = await personDocsGet({
          personUuid: uploadForPersonUuid,
          is_attachment: true,
        });
        if (isStale()) {
          throw new Error("Document refresh superseded");
        }
        applyPersonDocs(docs);
        setDocsLoadError(false);
        if (allSucceeded) {
          setOpenUploadModal(null);
        }
      } catch (err) {
        if (isStale()) {
          throw err;
        }
        showPushToast?.({
          message:
            "Files uploaded but failed to refresh the list. Please try again.",
          duration: 4000,
          type: "error",
        });
        throw new Error("Failed to refresh documents after upload");
      } finally {
        if (!isStale()) {
          setIsLoadingDocs(false);
        }
      }
    },
    [applyPersonDocs, personDocsGet, personUuid, showPushToast]
  );

  const isDeletingConsent = deletingCounts["consent-form"] > 0;
  const isDeletingCertificate = deletingCounts.other > 0;
  const isDeletingResume = deletingCounts.resume > 0;

  return (
    <>
      <DocsFormSection
        docs={localCertificateDocs}
        handleDelete={(doc) =>
          handleDeleteDoc(doc, "other", setLocalCertificateDocs)
        }
        handleAdd={() => setOpenUploadModal("other")}
        isDeleting={isDeletingCertificate}
        title="Certificates"
        enableDelete={canEdit360Reports}
        enableAdd={canEdit360Reports}
        isLoadingDocs={isLoadingDocs}
        docsLoadError={docsLoadError}
        onRetryLoadDocs={handleRetryLoadDocs}
      />
      <DocsFormSection
        docs={localResumeDocs}
        handleDelete={(doc) =>
          handleDeleteDoc(doc, "resume", setLocalResumeDocs)
        }
        handleAdd={() => setOpenUploadModal("resume")}
        isDeleting={isDeletingResume}
        title="Resume"
        enableDelete={canEdit360Reports}
        enableAdd={canEdit360Reports}
        isLoadingDocs={isLoadingDocs}
        docsLoadError={docsLoadError}
        onRetryLoadDocs={handleRetryLoadDocs}
      />
      <ConsentFormSection
        consentDocs={localConsentDocs}
        consentForms={consentForms}
        handleDelete={
          canEdit360Reports
            ? (doc) => handleDeleteDoc(doc, "consent-form", setLocalConsentDocs)
            : undefined
        }
        handleAddConsent={
          canEdit360Reports
            ? () => setOpenUploadModal("consent-form")
            : undefined
        }
        isDeleting={isDeletingConsent}
        isLoadingDocs={isLoadingDocs}
        docsLoadError={docsLoadError}
        onRetryLoadDocs={handleRetryLoadDocs}
      />

      {openUploadModal && (
        <Modal
          open
          onClose={() => setOpenUploadModal(null)}
          title={UPLOAD_MODAL_TITLES[openUploadModal]}
          width="w-full max-w-2xl"
        >
          <DragDropUploader
            key={personUuid}
            personUuid={personUuid}
            documentType={openUploadModal}
            onUpload={handleUploadComplete}
          />
        </Modal>
      )}
    </>
  );
};

export default PersonDocumentsSection;
